// src/v3/ui/pinbar/pinbar-detail.tsx

import { useState, type ComponentProps } from "react";
import { ChevronRight, Loader2 } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  formatDisplayDateTime,
  Markdown,
} from "../_common";
import {
  APPROVE_STATUS_HUMAN,
  APPROVE_STATUS_LABELS,
  APPROVE_STATUS_OBJ,
  APPROVE_STATUS_VARIANTS,
  PINBAR_STATUS_LABELS,
  PINBAR_STATUS_VARIANTS,
  PINBAR_TYPE_LABELS,
  PINBAR_TYPE_VARIANTS,
} from "../_deps";
import type { PinbarItem } from "./pinbar.hooks";

type BadgeVariant = NonNullable<ComponentProps<typeof Badge>["variant"]>;
const badgeVariants: readonly BadgeVariant[] = [
  "default",
  "secondary",
  "destructive",
  "emerald",
  "outline",
];

function resolveBadgeVariant(
  value: string | undefined,
  fallback: BadgeVariant,
): BadgeVariant {
  return badgeVariants.find((variant) => variant === value) ?? fallback;
}

// Replace technical criterion IDs with human-readable Russian names
const TECHNICAL_TO_HUMAN: Array<[RegExp, string]> = [
  [/pinbar_pattern/gi, "Структура пинбара"],
  [/buffer_position/gi, "Положение у границы канала"],
  [/trend_sync/gi, "Синхронность трендов"],
  [/entry_zone_filter/gi, "Зона входа — <50% отката"],
  [/days_to_earnings/gi, "Дни до отчёта"],
  [/shadowToBodyRatio/gi, "соотношение тень/тело"],
  [/pinbarCandle/gi, "сигнальная свеча"],
];

function humanizeReasoning(text: string): string {
  let result = text;
  for (const [pattern, replacement] of TECHNICAL_TO_HUMAN) {
    result = result.replace(pattern, replacement);
  }
  return result;
}

interface PinbarDetailProps {
  pinbar: PinbarItem;
  onUpdateField?: (
    id: string,
    field: string,
    value: string,
  ) => Promise<void>;
  onVerifyAi?: (id: string) => Promise<void>;
  onResetAi?: (id: string) => Promise<void>;
  isAiVerifying?: boolean;
  aiProgress?: { value: number | null; label?: string | undefined } | undefined;
}

function formatDate(d: Date | string | undefined): string {
  if (!d) return "—";
  const date = typeof d === "string" ? new Date(d) : d;
  return formatDisplayDateTime(date);
}

function statusLabel(
  labels: Record<string, Record<string, string>>,
  value: string,
): string {
  return labels[value]?.["en-US"] ?? value;
}

export function PinbarDetail({ pinbar, onUpdateField, onVerifyAi, onResetAi, isAiVerifying, aiProgress }: PinbarDetailProps) {
  const [editingComment, setEditingComment] = useState(false);
  const [commentDraft, setCommentDraft] = useState(pinbar.comment ?? "");
  const [savingComment, setSavingComment] = useState(false);
  const [contextOpen, setContextOpen] = useState(false);
  const [reasoningOpen, setReasoningOpen] = useState(false);

  const typeVariant = resolveBadgeVariant(
    Object.entries(PINBAR_TYPE_VARIANTS).find(([key]) => key === pinbar.type)?.[1],
    "outline",
  );
  const approveVariant = resolveBadgeVariant(
    Object.entries(APPROVE_STATUS_VARIANTS).find(
      ([key]) => key === pinbar.approveStatus,
    )?.[1],
    "secondary",
  );
  const pinbarStatusVariant = resolveBadgeVariant(
    Object.entries(PINBAR_STATUS_VARIANTS).find(
      ([key]) => key === pinbar.pinbarStatus,
    )?.[1],
    "outline",
  );

  const handleSaveComment = async () => {
    if (!onUpdateField) return;
    setSavingComment(true);
    try {
      await onUpdateField(pinbar.id, "comment", commentDraft);
      setEditingComment(false);
    } finally {
      setSavingComment(false);
    }
  };

  const handleApprove = async (status: string) => {
    if (!onUpdateField) return;
    await onUpdateField(pinbar.id, "approveStatus", status);
  };

  return (
    <Card className="border-0 shadow-none">
      <CardContent className="space-y-4">
        {/* Header: symbol + type chip + approve chip */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xl font-bold">{pinbar.symbol}</span>
          <Badge variant={typeVariant}>
            {statusLabel(PINBAR_TYPE_LABELS, pinbar.type)}
          </Badge>
          <Badge variant={approveVariant}>
            {statusLabel(APPROVE_STATUS_LABELS, pinbar.approveStatus)}
          </Badge>
        </div>

        {/* Status block: pinbarStatus chip */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Pinbar Status:</span>
          <Badge variant={pinbarStatusVariant}>
            {statusLabel(PINBAR_STATUS_LABELS, pinbar.pinbarStatus)}
          </Badge>
        </div>

        {/* Meta grid */}
        <div className="grid gap-2 sm:grid-cols-2">
          <div>
            <span className="text-xs text-muted-foreground">Signal Date</span>
            <div className="text-sm font-medium">
              {new Date(pinbar.date).toLocaleDateString()}
            </div>
          </div>
          <div>
            <span className="text-xs text-muted-foreground">Strategy Version</span>
            <div className="text-sm font-medium">{pinbar.strategyVersion}</div>
          </div>
          {pinbar.timestamp && (
            <>
              <div>
                <span className="text-xs text-muted-foreground">Created</span>
                <div className="text-sm font-medium">
                  {formatDate(pinbar.timestamp.createdAt)}
                </div>
              </div>
              <div>
                <span className="text-xs text-muted-foreground">Updated</span>
                <div className="text-sm font-medium">
                  {formatDate(pinbar.timestamp.updatedAt)}
                </div>
              </div>
            </>
          )}
        </div>

        {/* AI Reasoning — collapsible text */}
        {pinbar.reasoningText !== null && pinbar.reasoningText !== undefined && pinbar.reasoningText !== "" && (
          <div className="space-y-1">
            <button
              type="button"
              onClick={() => setReasoningOpen((v) => !v)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronRight
                className={`h-3 w-3 transition-transform ${reasoningOpen ? "rotate-90" : ""}`}
              />
              <span>AI Reasoning</span>
            </button>
            {reasoningOpen && (
              <div className="rounded-md bg-muted p-3 text-xs break-words max-h-96 overflow-y-auto">
                <Markdown className="text-xs" compact>{humanizeReasoning(pinbar.reasoningText)}</Markdown>
              </div>
            )}
          </div>
        )}

        {/* Comment — inline editable */}
        <div className="space-y-1">
          <span className="text-xs text-muted-foreground">Comment</span>
          {editingComment ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={commentDraft}
                onChange={(e) => setCommentDraft(e.target.value)}
                className="flex-1 rounded-md border px-2 py-1 text-sm"
                placeholder="Add comment..."
              />
              <Button size="sm" onClick={handleSaveComment} disabled={savingComment}>
                Save
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setEditingComment(false);
                  setCommentDraft(pinbar.comment ?? "");
                }}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <div
              className="text-sm cursor-pointer hover:bg-muted rounded px-1 py-0.5 -mx-1"
              onClick={() => {
                setCommentDraft(pinbar.comment ?? "");
                setEditingComment(true);
              }}
            >
              {pinbar.comment ? (
                <Markdown className="text-sm" compact>{pinbar.comment}</Markdown>
              ) : (
                <span className="text-muted-foreground italic">Click to add comment...</span>
              )}
            </div>
          )}
        </div>

        {/* Context — collapsible */}
        {pinbar.context !== null && pinbar.context !== undefined && (
          <div className="space-y-1">
            <button
              type="button"
              onClick={() => setContextOpen((v) => !v)}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronRight
                className={`h-3 w-3 transition-transform ${contextOpen ? "rotate-90" : ""}`}
              />
              <span>Context</span>
            </button>
            {contextOpen && (
              <div className="rounded-md bg-muted p-3 text-xs space-y-1 max-h-64 overflow-y-auto">
                {Object.entries(pinbar.context).map(([key, value]) => (
                  <div key={key} className="flex gap-2">
                    <span className="font-medium text-muted-foreground min-w-[120px]">{key}:</span>
                    <span className="font-mono break-all">
                      {typeof value === "object"
                        ? JSON.stringify(value)
                        : String(value)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* AI verification progress */}
        {isAiVerifying && (
          <div className="space-y-1.5 rounded-md bg-violet-50 border border-violet-200 p-3">
            <div className="flex items-center gap-2 text-xs text-violet-700">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span className="flex-1 truncate">
                {aiProgress?.label ?? "AI verification in progress..."}
              </span>
              {aiProgress?.value != null && (
                <span className="font-medium">{aiProgress.value}%</span>
              )}
            </div>
            {aiProgress?.value != null && (
              <div className="h-1.5 overflow-hidden rounded-full bg-violet-100">
                <div
                  className="h-full rounded-full bg-violet-500 transition-all duration-300"
                  style={{ width: `${aiProgress.value}%` }}
                />
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        {onUpdateField && (
          <div className="flex flex-wrap gap-2 pt-2 border-t">
            {APPROVE_STATUS_HUMAN.filter(
              (s) => s !== APPROVE_STATUS_OBJ.NEW,
            ).map((status) => (
              <Button
                key={status}
                size="sm"
                variant={pinbar.approveStatus === status ? "default" : "outline"}
                onClick={() => void handleApprove(status)}
              >
                {statusLabel(APPROVE_STATUS_LABELS, status)}
              </Button>
            ))}
            {onVerifyAi && (
              <Button
                size="sm"
                variant="outline"
                disabled={isAiVerifying}
                onClick={() => void onVerifyAi(pinbar.id)}
              >
                {isAiVerifying && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                AI Verify
              </Button>
            )}
            {onResetAi && (
              <Button
                size="sm"
                variant="outline"
                className="text-red-600 border-red-300 hover:bg-red-50"
                onClick={() => void onResetAi(pinbar.id)}
              >
                Reset AI
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
